import React, { useState, useEffect } from "react";
import { Divider, Grid, TextField, Typography } from "@mui/material";
import * as Yup from "yup";
import { updateUserOrg } from "@/store/slices/userSlice";
import regex from "@/utils/regex";

const ORG_FIELD_GETTERS = {
  name: (o) => o?.name,
  abn: (o) => o?.abn,
  acn: (o) => o?.acn,
  address: (o) => o?.address,
  phoneNumber: (o) => o?.phoneNumber,
  telephoneNumber: (o) => o?.telephoneNumber,
  email: (o) => o?.email,
  complaintsEmail: (o) => o?.complaintsEmail,
};

const getCurrentOrgValue = (organisation, fieldName) => {
  const getter = ORG_FIELD_GETTERS[fieldName];
  return getter ? getter(organisation) : organisation?.[fieldName];
};

const initialOrgFieldErrors = {
  acn: "",
  address: "",
  phoneNumber: "",
  telephoneNumber: "",
  email: "",
  complaintsEmail: "",
};

const orgValidationSchema = Yup.object({
  acn: Yup.string()
    .optional()
    .matches(regex.allowOnlyNumber, { message: "Please enter valid acn number", excludeEmptyString: true }),
  address: Yup.string()
    .optional()
    .matches(regex.addressRegex, { message: "Please enter organisation address", excludeEmptyString: true }),
  phoneNumber: Yup.string()
    .optional()
    .matches(regex.phoneNumber, { message: "Please enter valid organisation phone number", excludeEmptyString: true }),
  telephoneNumber: Yup.string()
    .optional()
    .matches(regex.telephoneNumber, {
      message: "Please enter valid organisation telephone number",
      excludeEmptyString: true,
    }),
  email: Yup.string().matches(regex.email, "Please enter organisation email"),
  complaintsEmail: Yup.string().matches(regex.email, "Please enter organisation complaints email"),
});

const getInitialOrgState = (organisation) => {
  if (!organisation) return null;
  return {
    name: organisation?.name || "",
    abn: organisation?.abn || "",
    acn: organisation?.acn || "",
    address: organisation?.address || "",
    phoneNumber: organisation?.phoneNumber || "",
    telephoneNumber: organisation?.telephoneNumber || "",
    email: organisation?.email || "",
    complaintsEmail: organisation?.complaintsEmail || "",
  };
};

const OrganisationDetailsSection = ({ organisation, orgId, dispatch, isOrgEditable, orgAccessComponent }) => {
  const [state, setState] = useState(null);
  const [fieldErrors, setFieldErrors] = useState(initialOrgFieldErrors);

  useEffect(() => {
    setState(getInitialOrgState(organisation));
  }, [organisation]);

  const validateField = async (fieldName, value) => {
    try {
      await orgValidationSchema.validateAt(fieldName, { [fieldName]: value });
      setFieldErrors((prev) => ({ ...prev, [fieldName]: "" }));
    } catch (error) {
      setFieldErrors((prev) => ({ ...prev, [fieldName]: error.message }));
    }
  };

  const handle = {
    onChangeField: (value, name) => {
      setState((prev) => (prev ? { ...prev, [name]: value } : prev));
      validateField(name, value);
    },
    onBlurField: (value, name) => {
      try {
        const oldVal = getCurrentOrgValue(organisation, name);
        if (oldVal === value) return;
        if (fieldErrors[name] !== "") return;

        setState((prev) => (prev ? { ...prev, [name]: value } : prev));

        if (!orgId) return;
        dispatch(updateUserOrg({ orgId, [name]: value }));
      } catch (error) {
        const newErrors = {};
        error?.inner?.forEach((err) => {
          newErrors[err.path] = err.message;
        });
        setFieldErrors((prev) => ({ ...prev, ...newErrors }));
      }
    },
  };

  if (!state) return null;

  return (
    <>
      <Grid item xs={8}>
        <Divider style={{ margin: "20px 0 10px 0" }} />
      </Grid>

      <Grid item xs={8}>
        <Typography variant="h5" style={{ margin: "0 0 5px 0" }}>
          Organisation details
        </Typography>
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="Organisation name"
          variant="filled"
          value={state.name}
          type="text"
          disabled
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="ABN"
          variant="filled"
          value={state.abn}
          type="text"
          disabled
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="ACN"
          variant="filled"
          value={state.acn}
          type="text"
          size="small"
          style={{ margin: "0 0 5px 0" }}
          inputProps={{ maxLength: 50 }}
          disabled={!isOrgEditable}
          onBlur={(e) => handle.onBlurField(e.target.value, "acn")}
          onChange={(e) => handle.onChangeField(e.target.value, "acn")}
          error={!!fieldErrors.acn}
          helperText={fieldErrors.acn}
          fullWidth
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="Address"
          variant="filled"
          type="text"
          value={state.address}
          error={!!fieldErrors.address}
          helperText={fieldErrors.address}
          onChange={(e) => handle.onChangeField(e.target.value, "address")}
          onBlur={(e) => handle.onBlurField(e.target.value, "address")}
          disabled={!isOrgEditable}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="Phone number"
          variant="filled"
          type="text"
          value={state.phoneNumber}
          error={!!fieldErrors.phoneNumber}
          helperText={fieldErrors.phoneNumber}
          onChange={(e) => handle.onChangeField(e.target.value, "phoneNumber")}
          onBlur={(e) => handle.onBlurField(e.target.value, "phoneNumber")}
          disabled={!isOrgEditable}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="Telephone number"
          variant="filled"
          type="text"
          value={state.telephoneNumber}
          error={!!fieldErrors.telephoneNumber}
          helperText={fieldErrors.telephoneNumber}
          onChange={(e) => handle.onChangeField(e.target.value, "telephoneNumber")}
          onBlur={(e) => handle.onBlurField(e.target.value, "telephoneNumber")}
          disabled={!isOrgEditable}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="Email"
          variant="filled"
          type="text"
          value={state.email}
          error={!!fieldErrors.email}
          helperText={fieldErrors.email}
          onChange={(e) => handle.onChangeField(e.target.value, "email")}
          onBlur={(e) => handle.onBlurField(e.target.value, "email")}
          disabled={!isOrgEditable}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      {orgAccessComponent.isOrgComplaintsEmail && (
        <Grid item xs={8}>
          <TextField
            label="Complaints Email"
            variant="filled"
            type="text"
            value={state.complaintsEmail}
            error={!!fieldErrors.complaintsEmail}
            helperText={fieldErrors.complaintsEmail}
            onChange={(e) => handle.onChangeField(e.target.value, "complaintsEmail")}
            onBlur={(e) => handle.onBlurField(e.target.value, "complaintsEmail")}
            disabled={!isOrgEditable}
            size="small"
            style={{ margin: "0 0 5px 0" }}
            fullWidth
          />
        </Grid>
      )}
    </>
  );
};

export default OrganisationDetailsSection;
